/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ConstructionWorksheetDto, ConstructionWorksheetScheduleTaskDto } from '@gh/builder-shared-data';
import {
  composeSetters,
  createArrayHelper,
  DateTimeTypes,
  EntityConfig,
  fieldStateByEntityConfig,
  fieldValidationSetter,
  fieldValidatorByEntityConfig,
  FormStateSnapshot,
  formStateSnapshot,
  getFormStateSnapshotValue,
  setDirty,
  setEntityField,
  TextTypes,
} from '@gh/common-shared';
import { EMPTY_ID, EntityVersion, ID, WithEntityState } from '@gh/common-shared-data';
import { FocusedCell } from '@gh/core-ui-grid';
import { currentNormalizedDate } from '@gh/core-util';
import { isEmpty, omit, pick, sortBy } from 'lodash';
import { JobScheduleFinderListItem } from './schedule-finder.model';

export type ConstructionWorksheetMasterInfo = {
  jobId: ID;
  scheduleId: ID;
  jobNumber: string;
  customerName: string;
  siteAddress: string;
};

export type ConstructionWorksheetTask = WithEntityState<ConstructionWorksheetScheduleTaskDto & {
  confirmedOnDateStartFrom: Date;
  confirmedForDateStartFrom: Date;
}>;

export type ConstructionWorksheetDetails = {
  masterInfo: FormStateSnapshot<ConstructionWorksheetMasterInfo>;
  tasks: ConstructionWorksheetTask[];
  jobVersion: EntityVersion;
  scheduleVersion: EntityVersion;
  isFinalized: boolean;
  isNotProceeding: boolean;
  showCompletedTasks: boolean;
  focusedCell?: FocusedCell;
};

export const getConstructionWorksheetTaskId = (task: ConstructionWorksheetTask) => task.scheduleTaskId;

export const CONSTRUCTION_WORKSHEET_DETAILS_INIT: ConstructionWorksheetDetails = {
  masterInfo: formStateSnapshot({
    jobId: EMPTY_ID,
    scheduleId: EMPTY_ID,
    jobNumber: '',
    customerName: '',
    siteAddress: '',
  }),
  tasks: [],
  scheduleVersion: 0,
  jobVersion: 0,
  showCompletedTasks: false,
  isFinalized: false,
  isNotProceeding: false,
};

const CONSTRUCTION_WORKSHEET_TASK_ENTITY_CONFIG: EntityConfig<ConstructionWorksheetTask> = {
  confirmedWith: {
    type: TextTypes.Text255,
  },
  confirmedOnDate: {
    type: DateTimeTypes.ShortDate,
  },
  confirmedForDate: {
    type: DateTimeTypes.ShortDate,
  },
};

export const constructionWorksheetTaskArr = createArrayHelper(getConstructionWorksheetTaskId);

export const validateConstructionWorksheetTaskField = fieldValidatorByEntityConfig(
  CONSTRUCTION_WORKSHEET_TASK_ENTITY_CONFIG);
export const constructionWorksheetTaskFieldState = fieldStateByEntityConfig(CONSTRUCTION_WORKSHEET_TASK_ENTITY_CONFIG);
export const setConstructionWorksheetTaskField = composeSetters(
  setEntityField,
  setDirty,
  fieldValidationSetter(validateConstructionWorksheetTaskField));

// tslint:disable-next-line:max-line-length
export const constructionWorksheetMasterInfoFromSchedule = (schedule: JobScheduleFinderListItem): ConstructionWorksheetMasterInfo => ({
  scheduleId: schedule.scheduleId,
  jobId: schedule.jobId,
  jobNumber: schedule.jobNumber,
  customerName: schedule.customerName,
  siteAddress: schedule.siteAddress,
});

export const constructionWorksheetDetailsFromDto = (dto: ConstructionWorksheetDto): ConstructionWorksheetDetails => {
  const currentDate = currentNormalizedDate();

  return {
    masterInfo: formStateSnapshot(pick(dto, 'jobId', 'scheduleId', 'jobNumber', 'customerName', 'siteAddress')),
    scheduleVersion: dto.scheduleVersion,
    jobVersion: dto.jobVersion,
    showCompletedTasks: false,
    tasks: sortBy(
      dto.tasks.map((task) => ({
        ...task,
        confirmedOnDateStartFrom: task.confirmedOnDate || currentDate,
        confirmedForDateStartFrom: task.confirmedForDate || currentDate,
      })),
      'taskNumber'),
    isFinalized: dto.isFinalized,
    isNotProceeding: dto.isNotProceeding,
  };
};

export const constructionWorksheetDetailsToDto = (model: ConstructionWorksheetDetails): ConstructionWorksheetDto => ({
  ...getFormStateSnapshotValue(model.masterInfo),
  scheduleVersion: model.scheduleVersion,
  jobVersion: model.jobVersion,
  tasks: model.tasks.map((task) =>
    <ConstructionWorksheetTask>omit(task, 'confirmedOnDateStartFrom', 'confirmedForDateStartFrom')),
  isFinalized: model.isFinalized,
  isNotProceeding: model.isNotProceeding,
});

export const isConstructionWorksheetJobEditable = ({ isFinalized, isNotProceeding }: ConstructionWorksheetDetails) =>
  !(isFinalized || isNotProceeding);

export const hasContructionWorksheetJob = ({ masterInfo }: ConstructionWorksheetDetails) =>
  !isEmpty(getFormStateSnapshotValue(masterInfo).jobId);
